// Ticket #172 — self-heal `.claude/.gitignore` cho workspace ĐÃ TỒN TẠI.
//
// Vì sao KHÔNG gọi writer của đường `init` (project-tree-scaffolder.ts, hàm ghi file gitignore):
// hàm đó ghi ĐÈ toàn file (writeTextAtomic với hằng template) ⇒ xoá sạch dòng user tự thêm. Điều
// kiện đóng của #172 là "idempotent, giữ dòng user tự thêm" ⇒ bắt buộc merge, không overwrite.
// ⛔ Đừng "gọn hoá" bằng cách gọi writer đó ở đây.
// (Tên hàm writer cố ý KHÔNG dán nguyên văn: gate #172 đếm tên đó trên src/ để chứng minh đường
//  `init` còn nguyên và `doctor` đã bỏ nó — mỗi lần trích lại trong comment là gate đỏ oan.)
//
// Vì sao không dùng mergeAvatarBlockIntoContent (avatar-block-text-ops.ts): file ngoài đời KHÔNG
// có marker `# === avatar ===` ⇒ nhánh no-marker của hàm đó append TRỌN block ⇒ mọi workspace cũ
// bị nhân đôi toàn bộ pattern. Ở đây là set-difference, khác bản chất — không phải nhân bản logic
// marker.
//
// TÍNH IDEMPOTENT nằm ở nhánh "thiếu = 0 ⇒ return, KHÔNG ghi" — không phải ở việc render lại
// chuỗi giống hệt. ⛔ Đừng đổi thành "ghi lại rồi so nội dung": thứ tự dòng và line-ending của
// user sẽ làm nó ghi mỗi lần.
//
// KHÔNG LOCK (chốt D5): withSettingsFileLock gác đúng file settings.json; dùng cho file này là sai
// ngữ nghĩa và sẽ throw reentrancy nếu bị gọi lồng. Đua tệ nhất = mất 1 append, sync kế tự lành.

import { lstat, readFile } from "node:fs/promises";
import { join } from "node:path";
import {
  CLAUDE_GITIGNORE_CONTENT,
  CLAUDE_GITIGNORE_REQUIRED_PATTERNS,
  findMissingClaudeGitignorePatterns,
} from "./claude-gitignore-required-patterns.js";
import { writeTextAtomic } from "./filesystem-helpers.js";

// ⚠️ Phần ASCII `Avatar (#172)` là LOAD-BEARING: gate build của phase-05 đo đúng chuỗi này trên
// `dist/index.js` để chứng minh bản vá có trong bundle (tên hàm bị minify, chuỗi tiếng Việt bị
// escape \uXXXX ⇒ chỉ literal ASCII này dùng được). Đổi wording = làm gate đỏ oan.
const APPEND_HEADER = "# Avatar (#172): pattern bảo vệ bổ sung tự động bởi 'avatar sync'.";

export type EnsureClaudeGitignoreStatus = "no-op" | "appended" | "created" | "failed";

export interface EnsureClaudeGitignoreResult {
  status: EnsureClaudeGitignoreStatus;
  added: string[];
  warning?: string;
}

// writeTextAtomic là tmp+rename, mà rename KHÔNG đi theo symlink: nó THAY THẾ symlink bằng file
// thường. Ai trỏ `.claude/.gitignore` sang chỗ khác (dotfiles dùng chung) sẽ mất liên kết ÂM THẦM
// — tệ hơn nữa là file đích vẫn khuyết rào nên lần sau vẫn "thiếu". Đường thoát giống hệt nhánh
// không-ghi-được (EACCES): KHÔNG ghi, trả "failed" kèm warning để call site warn ra cho người dùng.
// Chỉ gọi ở ngay TRƯỚC hai chỗ ghi — ca no-op phải giữ nguyên "no-op" (idempotent), không được
// biến thành cảnh báo giả cho workspace vốn đã đủ pattern.
async function refuseIfSymlink(path: string): Promise<EnsureClaudeGitignoreResult | null> {
  let isLink = false;
  try {
    isLink = (await lstat(path)).isSymbolicLink();
  } catch (err) {
    // ENOENT = không có gì ở đó (kể cả symlink) ⇒ ghi thoải mái. Lỗi khác để catch ngoài xử.
    if ((err as NodeJS.ErrnoException)?.code !== "ENOENT") throw err;
  }
  if (!isLink) return null;
  return {
    status: "failed",
    added: [],
    warning: `${path} là symlink — không ghi (ghi nguyên tử sẽ thay symlink bằng file thường). Sửa tay file đích rồi chạy lại.`,
  };
}

export async function ensureClaudeGitignoreHasRequiredPatterns(
  workspacePath: string,
): Promise<EnsureClaudeGitignoreResult> {
  const path = join(workspacePath, ".claude", ".gitignore");
  try {
    let existing: string | null = null;
    try {
      existing = await readFile(path, "utf8");
    } catch (err) {
      // ENOENT = file vắng (ca hợp lệ). Lỗi khác (EACCES, EISDIR) KHÔNG được coi là vắng —
      // ghi đè lên thứ ta không đọc được là hành vi phá hoại.
      if ((err as NodeJS.ErrnoException)?.code !== "ENOENT") throw err;
    }

    // Vắng hoặc rỗng/toàn whitespace → 0 dòng user để giữ ⇒ ghi bản chuẩn.
    // Gộp 2 ca vào 1 nhánh (KISS) và tránh file mở đầu bằng dòng trắng.
    if (existing === null || existing.trim() === "") {
      // Symlink treo (đích không tồn tại) rơi vào đây qua ENOENT của readFile — vẫn phải chặn.
      const refused = await refuseIfSymlink(path);
      if (refused) return refused;
      await writeTextAtomic(path, CLAUDE_GITIGNORE_CONTENT);
      return { status: "created", added: [...CLAUDE_GITIGNORE_REQUIRED_PATTERNS] };
    }

    const missing = findMissingClaudeGitignorePatterns(existing);
    if (missing.length === 0) return { status: "no-op", added: [] };

    // trimEnd() xử ca file KHÔNG có newline cuối: thiếu bước này thì pattern đầu tiên bị DÁN vào
    // dòng cuối của user (2 rule dính thành 1 dòng vô nghĩa) ⇒ mất cả hai.
    const next = `${existing.trimEnd()}\n${APPEND_HEADER}\n${missing.join("\n")}\n`;
    const refused = await refuseIfSymlink(path);
    if (refused) return refused;
    await writeTextAtomic(path, next);
    return { status: "appended", added: missing };
  } catch (err) {
    return {
      status: "failed",
      added: [],
      warning: `không cập nhật được ${path}: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}
